import React, { useState, useEffect } from 'react';
import { TrendingUp, Sparkles, ArrowRight, RefreshCw, Target, Clock } from 'lucide-react';
import { ProjectSynergy } from '../../types';
import { apiService } from '../../services/api';

export const SynergyAnalysis: React.FC = () => {
  const [synergies, setSynergies] = useState<ProjectSynergy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSynergies();
  }, []);

  const fetchSynergies = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getProjectSynergies();
      setSynergies(response.synergies);
    } catch (err) {
      console.error('Failed to fetch synergies:', err);
      setError('Failed to load synergy analysis');
    } finally {
      setLoading(false);
    }
  };

  const getSynergyColor = (score: number) => {
    if (score >= 80) return 'border-green-500 bg-green-50';
    if (score >= 60) return 'border-blue-500 bg-blue-50';
    if (score >= 40) return 'border-yellow-500 bg-yellow-50';
    return 'border-gray-500 bg-gray-50';
  };

  const getSynergyBadgeColor = (score: number) => {
    if (score >= 80) return 'bg-green-100 text-green-800';
    if (score >= 60) return 'bg-blue-100 text-blue-800';
    if (score >= 40) return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getSynergyLevel = (score: number) => {
    if (score >= 80) return 'High';
    if (score >= 60) return 'Medium';
    if (score >= 40) return 'Low';
    return 'Minimal';
  };

  const getRecommendation = (synergy: ProjectSynergy) => {
    const { synergyScore, dependencyTypes, avgStrength } = synergy;
    
    if (synergyScore >= 80) {
      return {
        title: 'Strong Synergy Detected',
        message: 'These projects have high interdependence. Consider coordinating development schedules and resource allocation.',
        action: 'Recommend parallel development'
      };
    } else if (synergyScore >= 60) {
      return {
        title: 'Good Synergy Potential',
        message: 'Projects share significant dependencies. Coordinate key milestones to maximize efficiency.',
        action: 'Schedule milestone alignment'
      };
    } else if (synergyScore >= 40) {
      return {
        title: 'Moderate Synergy',
        message: 'Some shared dependencies exist. Monitor for optimization opportunities.',
        action: 'Track for optimization'
      };
    } else {
      return {
        title: 'Limited Synergy',
        message: 'Few shared dependencies. Projects can be developed independently.',
        action: 'Independent development'
      };
    }
  };

  const calculateROIBenefit = (synergy: ProjectSynergy) => {
    // Simple ROI calculation based on synergy strength
    const baseROI = synergy.synergyScore * 0.5; // Base ROI from synergy
    const dependencyBonus = synergy.dependencyCount * 2; // Bonus for each dependency
    const strengthMultiplier = synergy.avgStrength / 5; // Strength multiplier
    
    return Math.round(baseROI + dependencyBonus * strengthMultiplier);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="text-center text-red-600">
          <TrendingUp className="w-8 h-8 mx-auto mb-2" />
          {error}
          <button
            onClick={fetchSynergies}
            className="btn-secondary mt-4"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (synergies.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="text-center text-gray-500 py-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <Sparkles className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Synergies Detected</h3>
          <p className="text-gray-500 mb-4">
            Create dependencies between projects to identify synergy opportunities
          </p>
          <button
            onClick={fetchSynergies}
            className="btn-secondary"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh Analysis
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            Project Synergy Analysis
          </h3>
          <button
            onClick={fetchSynergies}
            className="btn-secondary flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        <p className="text-gray-600 mb-6">
          Identify optimization opportunities through project interdependencies and shared resources
        </p>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <div className="flex items-center gap-2 text-purple-600 mb-1">
              <TrendingUp className="w-4 h-4" />
              High Synergy
            </div>
            <div className="text-2xl font-bold text-purple-900">
              {synergies.filter(s => s.synergyScore >= 80).length}
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <Target className="w-4 h-4" />
              Total Pairs
            </div>
            <div className="text-2xl font-bold text-blue-900">
              {synergies.length}
            </div>
          </div>
          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <div className="flex items-center gap-2 text-green-600 mb-1">
              <Clock className="w-4 h-4" />
              Avg Dependencies
            </div>
            <div className="text-2xl font-bold text-green-900">
              {Math.round(synergies.reduce((sum, s) => sum + s.dependencyCount, 0) / synergies.length)}
            </div>
          </div>
        </div>
      </div>

      {/* Synergy Cards */}
      <div className="space-y-4">
        {synergies.map((synergy, index) => {
          const recommendation = getRecommendation(synergy);
          const roiBenefit = calculateROIBenefit(synergy);

          return (
            <div key={index} className={`bg-white rounded-lg border-2 p-6 ${getSynergyColor(synergy.synergyScore)}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="font-semibold text-gray-900">
                      {synergy.project1.name}
                    </h4>
                    <div className="flex items-center gap-1">
                      <ArrowRight className="w-4 h-4 text-gray-400" />
                      <div className="w-2 h-2 rounded-full bg-gray-300" />
                      <ArrowRight className="w-4 h-4 text-gray-400" />
                    </div>
                    <h4 className="font-semibold text-gray-900">
                      {synergy.project2.name}
                    </h4>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                    <div className="flex items-center gap-1">
                      <span className="font-medium">Dependencies:</span>
                      <span>{synergy.dependencyCount}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="font-medium">Avg Strength:</span>
                      <span>{synergy.avgStrength}/5</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="font-medium">Avg Impact:</span>
                      <span>{synergy.avgImpact}%</span>
                    </div>
                  </div>

                  <div className="text-sm text-gray-700 mb-3">
                    <span className="font-medium">Dependency Types:</span> {synergy.dependencyTypes}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="text-center">
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${getSynergyBadgeColor(synergy.synergyScore)}`}>
                      {getSynergyLevel(synergy.synergyScore)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Score: {synergy.synergyScore}
                    </div>
                  </div>
                </div>
              </div>

              {/* Recommendation */}
              <div className="bg-white bg-opacity-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900 mb-1">
                      {recommendation.title}
                    </h5>
                    <p className="text-sm text-gray-600 mb-2">
                      {recommendation.message}
                    </p>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-medium">
                        {recommendation.action}
                      </span>
                      <span className="text-green-600 font-medium">
                        Est. ROI Benefit: +{roiBenefit}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mt-4">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Synergy Strength</span>
                  <span>{synergy.synergyScore}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-500 ${
                      synergy.synergyScore >= 80 ? 'bg-green-500' :
                      synergy.synergyScore >= 60 ? 'bg-blue-500' :
                      synergy.synergyScore >= 40 ? 'bg-yellow-500' :
                      'bg-gray-500'
                    }`}
                    style={{ width: `${Math.min(synergy.synergyScore, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom Actions */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="text-center">
          <h4 className="font-semibold text-gray-900 mb-2">
            Want to optimize your project synergies?
          </h4>
          <p className="text-gray-600 mb-4">
            Use the recommendations above to coordinate project schedules and maximize efficiency
          </p>
          <div className="flex justify-center gap-4">
            <button className="btn-primary">
              Export Recommendations
            </button>
            <button className="btn-secondary" onClick={fetchSynergies}>
              Recalculate Analysis
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

